import React, { useState } from 'react';
import { Monitor, Smartphone, Globe, Check, DollarSign, CreditCard, Eye, Edit3, X, Search, Server, Megaphone, PenTool, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const templates = [{
  id: 1,
  name: 'Agenttoboss (Enterprise SaaS)',
  desc: 'Salesforce-inspired CRM Command Center.',
  image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
  theme: {
    primary: 'bg-[#00A1E0]',
    text: 'text-[#00A1E0]',
    light: 'bg-[#F3F6F9]',
    border: 'border-[#D8E2EF]'
  },
  isSpecial: true,
  tab: 'agenttoboss'
}, {
  id: 2,
  name: 'ROI (Restaurant Intelligence)',
  desc: 'Premium Restaurant Operations & Financial Visibility.',
  image: 'https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&q=80&w=800',
  theme: {
    primary: 'bg-[#10B981]',
    text: 'text-[#10B981]',
    light: 'bg-[#0A0A0B]',
    border: 'border-white/10'
  },
  isSpecial: true,
  tab: 'roi-app'
}];
export function Storefront() {
  const [currency, setCurrency] = useState('USD');
  const [activeTemplate, setActiveTemplate] = useState(1);
  const [previewTemplate, setPreviewTemplate] = useState<number | null>(null);
  const getCurrencySymbol = () => {
    switch (currency) {
      case 'CAD':
        return 'C$';
      default:
        return '$';
    }
  };
  const activeTheme = previewTemplate ? templates.find(t => t.id === previewTemplate)?.theme : null;
  const sym = getCurrencySymbol();
  return <div className="space-y-8 max-w-[1600px] mx-auto" data-id="element-2647">
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2648">
        <div data-id="element-2649">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-2650">
            Storefront & Landing Pages
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-2651">
            Manage your conversion-optimized customer entry points.
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-2652">
          <div className="flex bg-gray-50 p-1 rounded-lg border border-gray-200" data-id="element-2653">
            {['USD', 'CAD'].map(c => <button key={c} onClick={() => setCurrency(c)} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${currency === c ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2654">
                {c}
              </button>)}
          </div>
          <button className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2" data-id="element-2655">
            <Globe size={18} data-id="element-2656" />
            Go Live
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2657">
        {templates.map(t => <motion.div key={t.id} whileHover={{
        y: -4
      }} className={`bg-white rounded-lg border-2 overflow-hidden group transition-all ${activeTemplate === t.id ? 'border-indigo-600 shadow-md' : 'border-gray-100 shadow-sm hover:border-gray-200'}`} data-id="element-2658">
            <div className="aspect-video relative overflow-hidden bg-gray-100" data-id="element-2659">
              <img src={t.image} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2660" />
              <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 px-4" data-id="element-2661">
                <button onClick={() => {
              if ((t as any).isSpecial) {
                window.dispatchEvent(new CustomEvent('changeTab', {
                  detail: (t as any).tab
                }));
              } else {
                setPreviewTemplate(t.id);
              }
            }} className="flex-1 py-2 bg-white text-slate-900 rounded-lg text-[10px] font-bold shadow-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-1.5" data-id="element-2662">
                  <Eye size={12} data-id="element-2663" />{' '}
                  {(t as any).isSpecial ? 'Open Site' : 'Preview'}
                </button>
                <button onClick={() => setActiveTemplate(t.id)} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-[10px] font-bold shadow-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-1.5" data-id="element-2664">
                  <Edit3 size={12} data-id="element-2665" /> Customize
                </button>
              </div>
              {activeTemplate === t.id && <div className="absolute top-3 right-3 bg-indigo-600 text-white px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 shadow-lg" data-id="element-2666">
                  <Check size={10} data-id="element-2667" /> Active
                </div>}
            </div>
            <div className="p-4" data-id="element-2668">
              <div className="flex items-center justify-between mb-1" data-id="element-2669">
                <p className="text-sm font-bold text-slate-900" data-id="element-2670">{t.name}</p>
                <div className="flex gap-1" data-id="element-2671">
                  <Monitor size={12} className="text-gray-400" data-id="element-2672" />
                  <Smartphone size={12} className="text-gray-400" data-id="element-2673" />
                </div>
              </div>
              <p className="text-xs text-gray-500 font-medium line-clamp-1" data-id="element-2674">
                {t.desc}
              </p>
            </div>
          </motion.div>)}
      </div>

      <div className="bg-slate-900 rounded-lg p-8 text-white relative overflow-hidden" data-id="element-2675">
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-2676">
          <div className="max-w-xl" data-id="element-2677">
            <h2 className="text-2xl font-bold mb-2" data-id="element-2678">Multi-Currency Engine</h2>
            <p className="text-slate-400 text-sm leading-relaxed" data-id="element-2679">
              Your storefront automatically detects customer location and
              displays pricing in {currency}. All payouts will be converted to
              your base currency at the mid-market rate.
            </p>
          </div>
          <div className="flex gap-4" data-id="element-2680">
            <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 flex items-center gap-4" data-id="element-2681">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center text-green-500" data-id="element-2682">
                <DollarSign size={20} data-id="element-2683" />
              </div>
              <div data-id="element-2684">
                <p className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-2685">
                  Current Rate
                </p>
                <p className="text-lg font-bold" data-id="element-2686">1 USD = 1.35 CAD</p>
              </div>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 flex items-center gap-4" data-id="element-2687">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-500" data-id="element-2688">
                <CreditCard size={20} data-id="element-2689" />
              </div>
              <div data-id="element-2690">
                <p className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-2691">
                  Settlement
                </p>
                <p className="text-lg font-bold" data-id="element-2692">T+2 Days</p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 blur-[100px] -mr-32 -mt-32" data-id="element-2693"></div>
      </div>

      {/* Full Screen Preview Modal */}
      <AnimatePresence data-id="element-2694">
        {previewTemplate && activeTheme && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 md:p-8" data-id="element-2695">
            <motion.div initial={{
          opacity: 0,
          y: 50,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          duration: 0.3
        }} className="w-full h-full max-w-7xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col relative" data-id="element-2696">
              {/* Modal Controls */}
              <div className="absolute top-4 right-4 z-50 flex gap-3" data-id="element-2697">
                <button onClick={() => {
              setActiveTemplate(previewTemplate);
              setPreviewTemplate(null);
            }} className={`px-4 py-2 ${activeTheme.primary} text-white rounded-lg text-sm font-bold shadow-lg flex items-center gap-2`} data-id="element-2698">
                  <Check size={16} data-id="element-2699" /> Activate This Template
                </button>
                <button onClick={() => setPreviewTemplate(null)} className="w-10 h-10 bg-white text-slate-900 rounded-lg shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors" data-id="element-2700">
                  <X size={20} data-id="element-2701" />
                </button>
              </div>

              {/* MOCK STOREFRONT CONTENT */}
              <div className="flex-1 overflow-y-auto bg-gray-50" data-id="element-2702">
                {/* Nav */}
                <nav className="bg-white border-b border-gray-100 px-8 py-4 flex items-center justify-between sticky top-0 z-40" data-id="element-2703">
                  <div className="flex items-center gap-2" data-id="element-2704">
                    <div className={`w-8 h-8 rounded ${activeTheme.primary} flex items-center justify-center text-white font-bold`} data-id="element-2705">
                      M
                    </div>
                    <span className="font-bold text-lg text-slate-900" data-id="element-2706">
                      MyReseller
                    </span>
                  </div>
                  <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600" data-id="element-2707">
                    <a href="#" className={`hover:${activeTheme.text}`} data-id="element-2708">
                      Hosting
                    </a>
                    <a href="#" className={`hover:${activeTheme.text}`} data-id="element-2709">
                      Domains
                    </a>
                    <a href="#" className={`hover:${activeTheme.text}`} data-id="element-2710">
                      Services
                    </a>
                    <a href="#" className={`hover:${activeTheme.text}`} data-id="element-2711">
                      Support
                    </a>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-2712">
                    <span className="text-sm font-bold text-slate-900" data-id="element-2713">
                      Login
                    </span>
                    <button className={`px-5 py-2 ${activeTheme.primary} text-white rounded-lg text-sm font-bold`} data-id="element-2714">
                      Get Started
                    </button>
                  </div>
                </nav>

                {/* Hero / Domain Search */}
                <div className={`${activeTheme.light} px-8 py-24 text-center border-b ${activeTheme.border}`} data-id="element-2715">
                  <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-2716">
                    Find Your Perfect Domain
                  </h1>
                  <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-2717">
                    Secure your online identity with our premium hosting and
                    domain registration services. Built for speed, security, and
                    scale.
                  </p>

                  <div className="max-w-3xl mx-auto bg-white p-2 rounded-xl shadow-lg flex items-center" data-id="element-2718">
                    <Search className="text-gray-400 ml-4" size={24} data-id="element-2719" />
                    <input type="text" placeholder="Enter your ideal domain name..." className="flex-1 px-4 py-4 text-lg focus:outline-none" data-id="element-2720" />
                    <button className={`px-8 py-4 ${activeTheme.primary} text-white rounded-lg font-bold text-lg whitespace-nowrap`} data-id="element-2721">
                      Search
                    </button>
                  </div>
                  <div className="mt-6 flex items-center justify-center gap-6 text-sm font-bold text-gray-500" data-id="element-2722">
                    <span data-id="element-2723">.com {sym}15.00</span>
                    <span data-id="element-2724">.ca {sym}12.00</span>
                    <span data-id="element-2725">.net {sym}14.00</span>
                  </div>
                </div>

                {/* Products Showcase */}
                <div className="px-8 py-20 max-w-7xl mx-auto" data-id="element-2726">
                  <div className="text-center mb-16" data-id="element-2727">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-2728">
                      Powerful Hosting Packages
                    </h2>
                    <p className="text-gray-500" data-id="element-2729">
                      Everything you need to get your business online.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24" data-id="element-2730">
                    {[{
                  name: 'Basic',
                  price: '35',
                  desc: 'Perfect for small sites.'
                }, {
                  name: 'Professional',
                  price: '65',
                  desc: 'For growing businesses.',
                  popular: true
                }, {
                  name: 'Enterprise',
                  price: '99',
                  desc: 'High performance needs.'
                }].map((plan, i) => <div key={i} className={`bg-white rounded-2xl p-8 border ${plan.popular ? `border-2 ${activeTheme.border} shadow-xl relative` : 'border-gray-200 shadow-sm'}`} data-id="element-2731">
                        {plan.popular && <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1 ${activeTheme.primary} text-white text-[10px] font-bold uppercase tracking-widest rounded-full`} data-id="element-2732">
                            Most Popular
                          </div>}
                        <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2733">
                          {plan.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-6" data-id="element-2734">
                          {plan.desc}
                        </p>
                        <div className="mb-8" data-id="element-2735">
                          <span className="text-4xl font-bold text-slate-900" data-id="element-2736">
                            {sym}
                            {plan.price}
                          </span>
                          <span className="text-gray-500" data-id="element-2737">/mo</span>
                        </div>
                        <button className={`w-full py-3 rounded-lg font-bold transition-colors ${plan.popular ? `${activeTheme.primary} text-white` : `${activeTheme.light} ${activeTheme.text} hover:bg-gray-100`}`} data-id="element-2738">
                          Select Plan
                        </button>
                      </div>)}
                  </div>

                  <div className="text-center mb-16" data-id="element-2739">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-2740">
                      Digital Services & Tools
                    </h2>
                    <p className="text-gray-500" data-id="element-2741">
                      Grow your brand with our expert services and AVA Suite.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2742">
                    <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center text-center" data-id="element-2743">
                      <div className={`w-16 h-16 rounded-2xl ${activeTheme.light} ${activeTheme.text} flex items-center justify-center mb-6`} data-id="element-2744">
                        <Megaphone size={32} data-id="element-2745" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2746">
                        Digital Marketing
                      </h3>
                      <p className="text-gray-500 text-sm mb-6" data-id="element-2747">
                        SEO, Social Media, and PPC campaigns to drive traffic.
                      </p>
                      <span className={`mt-auto font-bold ${activeTheme.text}`} data-id="element-2748">
                        From {sym}300/mo
                      </span>
                    </div>
                    <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center text-center" data-id="element-2749">
                      <div className={`w-16 h-16 rounded-2xl ${activeTheme.light} ${activeTheme.text} flex items-center justify-center mb-6`} data-id="element-2750">
                        <PenTool size={32} data-id="element-2751" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2752">
                        Graphic Design
                      </h3>
                      <p className="text-gray-500 text-sm mb-6" data-id="element-2753">
                        Logos, branding, and marketing materials.
                      </p>
                      <span className={`mt-auto font-bold ${activeTheme.text}`} data-id="element-2754">
                        From {sym}250/mo
                      </span>
                    </div>
                    <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center text-center" data-id="element-2755">
                      <div className={`w-16 h-16 rounded-2xl ${activeTheme.light} ${activeTheme.text} flex items-center justify-center mb-6`} data-id="element-2756">
                        <Shield size={32} data-id="element-2757" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2758">
                        AVA Security Suite
                      </h3>
                      <p className="text-gray-500 text-sm mb-6" data-id="element-2759">
                        Advanced monitoring and DDoS protection for your sites.
                      </p>
                      <span className={`mt-auto font-bold ${activeTheme.text}`} data-id="element-2760">
                        From {sym}15/mo
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <footer className="bg-slate-900 text-slate-400 py-12 px-8" data-id="element-2761">
                  <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-2762">
                    <div data-id="element-2763">
                      <div className="flex items-center gap-2 mb-6" data-id="element-2764">
                        <div className={`w-8 h-8 rounded ${activeTheme.primary} flex items-center justify-center text-white font-bold`} data-id="element-2765">
                          M
                        </div>
                        <span className="font-bold text-lg text-white" data-id="element-2766">
                          MyReseller
                        </span>
                      </div>
                      <p className="text-sm" data-id="element-2767">
                        Empowering businesses online since 2023.
                      </p>
                    </div>
                    <div data-id="element-2768">
                      <h4 className="text-white font-bold mb-4" data-id="element-2769">Services</h4>
                      <ul className="space-y-2 text-sm" data-id="element-2770">
                        <li data-id="element-2771">
                          <a href="#" className="hover:text-white" data-id="element-2772">
                            Web Hosting
                          </a>
                        </li>
                        <li data-id="element-2773">
                          <a href="#" className="hover:text-white" data-id="element-2774">
                            Domains
                          </a>
                        </li>
                        <li data-id="element-2775">
                          <a href="#" className="hover:text-white" data-id="element-2776">
                            Marketing
                          </a>
                        </li>
                      </ul>
                    </div>
                    <div data-id="element-2777">
                      <h4 className="text-white font-bold mb-4" data-id="element-2778">Support</h4>
                      <ul className="space-y-2 text-sm" data-id="element-2779">
                        <li data-id="element-2780">
                          <a href="#" className="hover:text-white" data-id="element-2781">
                            Help Center
                          </a>
                        </li>
                        <li data-id="element-2782">
                          <a href="#" className="hover:text-white" data-id="element-2783">
                            Contact Us
                          </a>
                        </li>
                        <li data-id="element-2784">
                          <a href="#" className="hover:text-white" data-id="element-2785">
                            System Status
                          </a>
                        </li>
                      </ul>
                    </div>
                    <div data-id="element-2786">
                      <h4 className="text-white font-bold mb-4" data-id="element-2787">Legal</h4>
                      <ul className="space-y-2 text-sm" data-id="element-2788">
                        <li data-id="element-2789">
                          <a href="#" className="hover:text-white" data-id="element-2790">
                            Privacy Policy
                          </a>
                        </li>
                        <li data-id="element-2791">
                          <a href="#" className="hover:text-white" data-id="element-2792">
                            Terms of Service
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </footer>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}